import {
  createContext,
  useContext,
  ReactNode,
  useMemo,
  useCallback,
  useEffect
} from 'react';
import { useQuery } from '@tanstack/react-query';
import { Product as ProductDTO } from '@setvi/shared/interfaces';
import type { QuoteItemDTO } from '@setvi/shared/services/react-query';
import { getQuoteV2StatusesQuery } from 'Services/Query/QuotesV2';
import type { QuoteV2, QuoteV2Status } from 'Services/Query/QuotesV2/Types';
import { Moment } from 'moment';
import {
  useQuote,
  useQuoteItems,
  useAddProducts,
  useAddFreehand,
  useRemoveQuoteItem,
  useAddSection,
  useAddSectionAndMoveItem,
  useUpdateSection,
  useUpdateQuoteStatus,
  useRemoveSectionKeepItems,
  useRefreshPrices,
  useMoveItem,
  useMoveItems,
  useRefreshSinglePrice,
  useReorderItems,
  useDebouncedItemUpdate,
  useRenewQuote,
  useUpdateItemQuantity,
  useUpdateItemNote,
  useUpdateItemLabels,
  useQuoteCache,
  useAddSubitem,
  useAddFreehandSubitem,
  useDeleteSubitem
} from '../hooks';
import type {
  AddFreehandVariables,
  AddFreehandSubitemVariables
} from '../hooks';

// =============================================================================
// Context Types - Split into focused interfaces to enable granular subscriptions
// =============================================================================

/** Quote details (without items) */
interface QuoteDataContextValue {
  quoteId: string;
  quote: QuoteV2 | undefined;
  isLoading: boolean;
  error: any;
  statuses: QuoteV2Status[];
  currentStatus: QuoteV2Status | null;
  isLoadingStatuses: boolean;
}

/** Quote items - fetched separately via v2 items/metadata endpoint */
interface QuoteItemsDataContextValue {
  items: QuoteItemDTO[];
  isLoadingItems: boolean;
  itemsError: any;
}

/** Stable mutation functions - rarely change */
interface QuoteMutationsContextValue {
  updateQuote: (updates: Partial<QuoteV2>) => void;
  addProducts: (params: {
    products: ProductDTO[];
    parentId?: string | null;
    afterItemId?: string | null;
  }) => Promise<any>;
  addFreehand: (params: AddFreehandVariables) => Promise<any>;
  removeItem: (itemId: string) => void;
  moveItem: (itemId: string, targetSectionId: string | null) => void;
  moveItems: (itemIds: string[], targetSectionId: string | null) => void;
  addSection: (params: {
    name: string;
    description?: string;
    afterItemId?: string | null;
  }) => void;
  addSectionAndMoveItem: (params: {
    name: string;
    description?: string;
    itemId: string;
  }) => void;
  updateSection: (params: {
    sectionId: string;
    name: string;
    description?: string;
  }) => void;
  removeSection: (sectionId: string) => void;
  removeSectionKeepItems: (sectionId: string) => void;
  refreshPrices: () => void;
  refreshSinglePrice: (params: { productId: string; sku: string }) => void;
  reorderItems: (
    moves: import('../hooks/use-reorder-items').ReorderMove[],
    newSections?: import('../hooks/use-reorder-items').NewSectionSpec[]
  ) => Promise<any>;
  updateQuoteStatus: (statusId: string) => void;
  renewQuote: (params: { validUntil: Date | Moment }) => Promise<any>;
  updateItemQuantity: {
    mutate: (params: { itemId: string; value: number }) => void;
  };
  updateItem: {
    mutate: (params: {
      itemId: string;
      updates: {
        sell?: number;
        totalGrossProfit?: number;
        totalGrossProfitPercentage?: number;
      };
    }) => void;
    cancel: () => void;
    lock: () => void;
    unlock: () => void;
  };
  updateItemNote: {
    mutate: (params: { itemId: string; note: string }) => void;
    cancel: () => void;
  };
  updateItemLabels: {
    mutate: (params: {
      itemId: string;
      labelType: string;
      customLabel: string;
      includeInTotalCost: boolean;
    }) => void;
  };
  addSubitems: (params: {
    itemId: string;
    products: ProductDTO[];
  }) => Promise<any>;
  addFreehandSubitem: (params: AddFreehandSubitemVariables) => Promise<any>;
  deleteSubitem: (params: { itemId: string; subItemId: string }) => void;
}

/** Loading/status flags - change during mutations */
interface QuoteStatusContextValue {
  isSaving: boolean;
  hasSaveError: boolean;
  isAddingProducts: boolean;
  isAddingFreehand: boolean;
  isAddingSubitems: boolean;
  isAddingFreehandSubitem: boolean;
  isRefreshingPrices: boolean;
  isRefreshingSinglePrice: boolean;
  isReorderingItems: boolean;
  isUpdatingQuoteStatus: boolean;
  isRenewingQuote: boolean;
}

// =============================================================================
// Contexts - Separate contexts enable granular subscriptions
// =============================================================================

export const QuoteDataContext = createContext<QuoteDataContextValue | null>(
  null
);
export const QuoteItemsDataContext =
  createContext<QuoteItemsDataContextValue | null>(null);
export const QuoteMutationsContext =
  createContext<QuoteMutationsContextValue | null>(null);
export const QuoteStatusContext = createContext<QuoteStatusContextValue | null>(
  null
);

export interface QuoteProviderProps {
  children: ReactNode;
  quoteId: string;
}

/**
 * Quote Context Provider
 * Fetches quote details and items separately via v2 endpoints
 */
export const QuoteProvider = ({ children, quoteId }: QuoteProviderProps) => {
  // Fetch quote details (without items)
  const { data: quote, isLoading, error } = useQuote(quoteId);

  // If the quote GET returned 401/403, the user has no access — don't fetch
  // items metadata for a quote they can't see.
  const accessErrorStatus = (error as any)?.response?.status;
  const hasAccessError = accessErrorStatus === 401 || accessErrorStatus === 403;

  // Fetch items separately
  const {
    data: items,
    isLoading: isLoadingItems,
    error: itemsError
  } = useQuoteItems(quoteId, !hasAccessError);

  // Cache operations
  const cache = useQuoteCache(quoteId);

  // Update quote in cache (memoized to prevent unnecessary re-renders)
  const updateQuote = useCallback(
    (updates: Partial<QuoteV2>) => {
      cache.setQuote(old => (old ? { ...old, ...updates } : old));
    },
    [cache]
  );

  // Fetch quote statuses
  const { data: statuses = [], isFetching: isLoadingStatuses } = useQuery(
    getQuoteV2StatusesQuery()
  );

  // Get current status object
  const currentStatus = useMemo(
    () => statuses.find(s => s._id === quote?.statusId) || null,
    [statuses, quote?.statusId]
  );

  // Immediate save mutations
  const addProductsMutation = useAddProducts(quoteId);
  const addFreehandMutation = useAddFreehand(quoteId);
  const removeQuoteItemMutation = useRemoveQuoteItem(quoteId);
  const moveItemMutation = useMoveItem(quoteId);
  const moveItemsMutation = useMoveItems(quoteId);
  const addSectionMutation = useAddSection(quoteId);
  const addSectionAndMoveItemMutation = useAddSectionAndMoveItem(quoteId);
  const updateSectionMutation = useUpdateSection(quoteId);
  const updateQuoteStatusMutation = useUpdateQuoteStatus(quoteId);
  const removeSectionKeepItemsMutation = useRemoveSectionKeepItems(quoteId);
  const refreshPricesMutation = useRefreshPrices(quoteId);
  const refreshSinglePriceMutation = useRefreshSinglePrice(quoteId);
  const reorderItemsMutation = useReorderItems(quoteId);
  const renewQuoteMutation = useRenewQuote(quoteId);
  const updateItemQuantityMutation = useUpdateItemQuantity(quoteId);
  const updateItemNoteMutation = useUpdateItemNote(quoteId);
  const updateItemLabelsMutation = useUpdateItemLabels(quoteId);
  const addSubitemMutation = useAddSubitem(quoteId);
  const addFreehandSubitemMutation = useAddFreehandSubitem(quoteId);
  const deleteSubitemMutation = useDeleteSubitem(quoteId);

  // Debounced item update - applies changes immediately, debounces API call
  const debouncedItemUpdate = useDebouncedItemUpdate(quoteId, 1500);

  // Unlock debounced updates when refresh completes
  useEffect(() => {
    if (
      !refreshPricesMutation.isLoading &&
      !refreshSinglePriceMutation.isLoading
    ) {
      debouncedItemUpdate.unlock();
    }
  }, [
    refreshPricesMutation.isLoading,
    refreshSinglePriceMutation.isLoading,
    debouncedItemUpdate
  ]);

  // Derived saving status flags (passed to SavingStatus component)
  const isSaving =
    addProductsMutation.isLoading ||
    removeQuoteItemMutation.isLoading ||
    moveItemMutation.isLoading ||
    moveItemsMutation.isLoading ||
    addSectionMutation.isLoading ||
    addSectionAndMoveItemMutation.isLoading ||
    updateSectionMutation.isLoading ||
    updateQuoteStatusMutation.isLoading ||
    removeSectionKeepItemsMutation.isLoading ||
    reorderItemsMutation.isLoading ||
    updateItemQuantityMutation.isLoading ||
    addSubitemMutation.isLoading ||
    addFreehandSubitemMutation.isLoading ||
    deleteSubitemMutation.isLoading ||
    debouncedItemUpdate.isLoading ||
    updateItemNoteMutation.isLoading ||
    updateItemLabelsMutation.isLoading;

  const hasSaveError =
    addProductsMutation.isError ||
    removeQuoteItemMutation.isError ||
    moveItemMutation.isError ||
    moveItemsMutation.isError ||
    addSectionMutation.isError ||
    addSectionAndMoveItemMutation.isError ||
    updateSectionMutation.isError ||
    updateQuoteStatusMutation.isError ||
    removeSectionKeepItemsMutation.isError ||
    reorderItemsMutation.isError ||
    updateItemQuantityMutation.isError ||
    addSubitemMutation.isError ||
    addFreehandSubitemMutation.isError ||
    deleteSubitemMutation.isError ||
    !!debouncedItemUpdate.error ||
    !!updateItemNoteMutation.error ||
    updateItemLabelsMutation.isError;

  // Wrapper functions for simpler API (memoized to prevent unnecessary re-renders)
  const removeItem = useCallback(
    (itemId: string) =>
      removeQuoteItemMutation.mutate({ itemId, itemType: 'product' }),
    [removeQuoteItemMutation]
  );

  const removeSection = useCallback(
    (sectionId: string) =>
      removeQuoteItemMutation.mutate({
        itemId: sectionId,
        itemType: 'section'
      }),
    [removeQuoteItemMutation]
  );

  const moveItem = useCallback(
    (itemId: string, targetSectionId: string | null) =>
      moveItemMutation.mutate({ itemId, targetSectionId }),
    [moveItemMutation]
  );

  const moveItems = useCallback(
    (itemIds: string[], targetSectionId: string | null) =>
      moveItemsMutation.mutate({ itemIds, targetSectionId }),
    [moveItemsMutation]
  );

  const updateQuoteStatus = useCallback(
    (statusId: string) => updateQuoteStatusMutation.mutate({ statusId }),
    [updateQuoteStatusMutation]
  );

  const reorderItems = useCallback(
    (
      moves: import('../hooks/use-reorder-items').ReorderMove[],
      newSections?: import('../hooks/use-reorder-items').NewSectionSpec[]
    ) => reorderItemsMutation.mutateAsync({ moves, newSections }),
    [reorderItemsMutation]
  );

  // Lock debounced updates and fire refresh immediately
  const refreshPrices = useCallback(() => {
    debouncedItemUpdate.lock();
    refreshPricesMutation.mutate();
  }, [debouncedItemUpdate, refreshPricesMutation]);

  // Lock debounced updates and fire single price refresh immediately
  const refreshSinglePrice = useCallback(
    (params: { productId: string; sku: string }) => {
      debouncedItemUpdate.lock();
      refreshSinglePriceMutation.mutate(params);
    },
    [debouncedItemUpdate, refreshSinglePriceMutation]
  );

  // =============================================================================
  // Split context values - each memoized separately for granular re-renders
  // =============================================================================

  // Data context - changes when quote data changes
  const dataValue: QuoteDataContextValue = useMemo(
    () => ({
      quoteId,
      quote,
      isLoading,
      error,
      statuses,
      currentStatus,
      isLoadingStatuses
    }),
    [
      quoteId,
      quote,
      isLoading,
      error,
      statuses,
      currentStatus,
      isLoadingStatuses
    ]
  );

  // Items data context - changes when items change
  const itemsDataValue: QuoteItemsDataContextValue = useMemo(
    () => ({
      items: items || [],
      isLoadingItems,
      itemsError
    }),
    [items, isLoadingItems, itemsError]
  );

  // Mutations context - stable functions, rarely changes
  const mutationsValue: QuoteMutationsContextValue = useMemo(
    () => ({
      updateQuote,
      addProducts: addProductsMutation.mutateAsync,
      addFreehand: addFreehandMutation.mutateAsync,
      removeItem,
      moveItem,
      moveItems,
      addSection: addSectionMutation.mutate,
      addSectionAndMoveItem: addSectionAndMoveItemMutation.mutate,
      updateSection: updateSectionMutation.mutate,
      removeSection,
      removeSectionKeepItems: removeSectionKeepItemsMutation.mutate,
      refreshPrices,
      refreshSinglePrice,
      reorderItems,
      updateQuoteStatus,
      renewQuote: renewQuoteMutation.mutateAsync,
      updateItemQuantity: updateItemQuantityMutation,
      updateItem: debouncedItemUpdate,
      updateItemNote: updateItemNoteMutation,
      updateItemLabels: updateItemLabelsMutation,
      addSubitems: addSubitemMutation.mutateAsync,
      addFreehandSubitem: addFreehandSubitemMutation.mutateAsync,
      deleteSubitem: deleteSubitemMutation.mutate
    }),
    [
      updateQuote,
      addProductsMutation.mutateAsync,
      addFreehandMutation.mutateAsync,
      removeItem,
      moveItem,
      moveItems,
      addSectionMutation.mutate,
      addSectionAndMoveItemMutation.mutate,
      updateSectionMutation.mutate,
      removeSection,
      removeSectionKeepItemsMutation.mutate,
      refreshPrices,
      refreshSinglePrice,
      reorderItems,
      updateQuoteStatus,
      renewQuoteMutation.mutateAsync,
      updateItemQuantityMutation,
      debouncedItemUpdate,
      updateItemNoteMutation,
      updateItemLabelsMutation,
      addSubitemMutation.mutateAsync,
      addFreehandSubitemMutation.mutateAsync,
      deleteSubitemMutation.mutate
    ]
  );

  // Status context - changes during mutations
  const statusValue: QuoteStatusContextValue = useMemo(
    () => ({
      isSaving,
      hasSaveError,
      isAddingProducts: addProductsMutation.isLoading,
      isAddingFreehand: addFreehandMutation.isLoading,
      isAddingSubitems: addSubitemMutation.isLoading,
      isAddingFreehandSubitem: addFreehandSubitemMutation.isLoading,
      isRefreshingPrices: refreshPricesMutation.isLoading,
      isRefreshingSinglePrice: refreshSinglePriceMutation.isLoading,
      isReorderingItems: reorderItemsMutation.isLoading,
      isUpdatingQuoteStatus: updateQuoteStatusMutation.isLoading,
      isRenewingQuote: renewQuoteMutation.isLoading
    }),
    [
      isSaving,
      hasSaveError,
      addProductsMutation.isLoading,
      addFreehandMutation.isLoading,
      addSubitemMutation.isLoading,
      addFreehandSubitemMutation.isLoading,
      refreshPricesMutation.isLoading,
      refreshSinglePriceMutation.isLoading,
      reorderItemsMutation.isLoading,
      updateQuoteStatusMutation.isLoading,
      renewQuoteMutation.isLoading
    ]
  );

  return (
    <QuoteDataContext.Provider value={dataValue}>
      <QuoteItemsDataContext.Provider value={itemsDataValue}>
        <QuoteMutationsContext.Provider value={mutationsValue}>
          <QuoteStatusContext.Provider value={statusValue}>
            {children}
          </QuoteStatusContext.Provider>
        </QuoteMutationsContext.Provider>
      </QuoteItemsDataContext.Provider>
    </QuoteDataContext.Provider>
  );
};

// =============================================================================
// Hooks - Use specific hooks for better performance
// =============================================================================

/**
 * Hook to access quote data (without items)
 * Use this when you only need to read quote metadata
 */
export const useQuoteData = () => {
  const context = useContext(QuoteDataContext);

  if (!context) {
    throw new Error('useQuoteData must be used within QuoteProvider');
  }

  return context;
};

/**
 * Hook to access quote items (fetched separately)
 * Use this when you need the items list
 */
export const useQuoteItemsData = () => {
  const context = useContext(QuoteItemsDataContext);

  if (!context) {
    throw new Error('useQuoteItemsData must be used within QuoteProvider');
  }

  return context;
};

/**
 * Hook to access mutation functions
 * Use this when you only need to perform mutations
 * Components using this will NOT re-render when quote data changes
 */
export const useQuoteMutations = () => {
  const context = useContext(QuoteMutationsContext);

  if (!context) {
    throw new Error('useQuoteMutations must be used within QuoteProvider');
  }

  return context;
};

/**
 * Hook to access loading/status flags
 * Use this when you need to show loading or error states
 */
export const useQuoteStatusFlags = () => {
  const context = useContext(QuoteStatusContext);

  if (!context) {
    throw new Error('useQuoteStatusFlags must be used within QuoteProvider');
  }

  return context;
};
